import React from 'react';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import type { InlineEditVariant, SelectOption } from './types';
import {
  CtrlEnterHint,
  ColorInput,
  ColorPresetsRow,
  ColorPresetButton,
} from './InlineEdit.styled';

interface InlineEditInputProps {
  variant: InlineEditVariant;
  value: string | number | boolean;
  onChange: (value: string | number | boolean) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onBlur: () => void;
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>;
  disabled: boolean;
  placeholder?: string;
  ariaLabel: string;
  options?: SelectOption[];
  min?: number;
  max?: number;
  step?: number;
  colorPresets?: string[];
  isImmediate: boolean;
}

export default function InlineEditInput({
  variant,
  value,
  onChange,
  onKeyDown,
  onBlur,
  inputRef,
  disabled,
  placeholder,
  ariaLabel,
  options,
  min,
  max,
  step,
  colorPresets,
  isImmediate,
}: InlineEditInputProps) {
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const newValue = variant === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
    if (isImmediate) {
      onChange(newValue);
    } else {
      onChange(newValue);
    }
  };

  switch (variant) {
    case 'textarea':
      return (
        <div>
          <Form.Control
            as="textarea"
            rows={2}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={String(value)}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={ariaLabel}
            size="sm"
          />
          <CtrlEnterHint>{t('inlineEdit.ctrlEnterHint')}</CtrlEnterHint>
        </div>
      );

    case 'number':
      return (
        <Form.Control
          type="number"
          ref={inputRef as React.RefObject<HTMLInputElement>}
          value={value === '' ? '' : Number(value)}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
          min={min}
          max={max}
          step={step}
          size="sm"
        />
      );

    case 'select':
      return (
        <Form.Select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={ariaLabel}
          size="sm"
        >
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      );

    case 'color':
      return (
        <div>
          <ColorInput
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={String(value) || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            aria-label={ariaLabel}
          />
          {colorPresets && colorPresets.length > 0 && (
            <ColorPresetsRow>
              {colorPresets.map((color) => (
                <ColorPresetButton
                  key={color}
                  type="button"
                  $color={color}
                  $selected={value === color}
                  onClick={() => onChange(color)}
                  disabled={disabled}
                  aria-label={`${ariaLabel} ${color}`}
                />
              ))}
            </ColorPresetsRow>
          )}
        </div>
      );

    case 'switch':
      return (
        <Form.Check
          type="switch"
          ref={inputRef as React.RefObject<HTMLInputElement>}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-label={ariaLabel}
        />
      );

    case 'text':
    case 'email':
    default:
      return (
        <Form.Control
          type={variant}
          ref={inputRef as React.RefObject<HTMLInputElement>}
          value={String(value)}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
          size="sm"
        />
      );
  }
}
